import { StateCreator } from "zustand";
import { OnboardingState } from "../services/User";
import { markTourSeen as apiMarkTourSeen, dismissTip as apiDismissTip } from "../services/Onboarding";

export interface OnboardingSlice {
  /** Id of the tour currently running, or null. Only one runs at a time. */
  activeTourId: string | null;
  /** Tour ids the user has finished or skipped. */
  seenTours: string[];
  /** One-time tip ids the user has dismissed. */
  dismissedTips: string[];

  /** Start a tour (no-op if one is already active). */
  startTour: (tourId: string) => void;
  /** Stop the active tour without marking it seen (e.g. route change). */
  endTour: () => void;
  /** Mark a tour finished/skipped: persists locally + to the backend. */
  markTourSeen: (tourId: string, completed?: boolean) => void;
  /** Dismiss a one-time tip: persists locally + to the backend. */
  dismissTip: (tipId: string) => void;
  /** Seed seen tours / dismissed tips from the server's user.onboarding. */
  hydrateOnboarding: (state: OnboardingState | null | undefined) => void;

  hasSeenTour: (tourId: string) => boolean;
  hasDismissedTip: (tipId: string) => boolean;
}

const union = (a: string[], b: string[]): string[] =>
  Array.from(new Set([...a, ...b]));

export const createOnboardingSlice: StateCreator<
  OnboardingSlice,
  [],
  [],
  OnboardingSlice
> = (set, get) => ({
  activeTourId: null,
  seenTours: [],
  dismissedTips: [],

  startTour: (tourId: string) => {
    if (get().activeTourId) return;
    set(() => ({ activeTourId: tourId }));
  },

  endTour: () => set(() => ({ activeTourId: null })),

  markTourSeen: (tourId: string, completed = false) => {
    set((state) => ({
      activeTourId: null,
      seenTours: union(state.seenTours, [tourId]),
    }));
    // Fire-and-forget backend sync; UI already updated optimistically.
    apiMarkTourSeen(tourId, completed).catch((err) =>
      console.error("[Onboarding] Failed to persist tour seen:", err),
    );
  },

  dismissTip: (tipId: string) => {
    set((state) => ({
      dismissedTips: union(state.dismissedTips, [tipId]),
    }));
    apiDismissTip(tipId).catch((err) =>
      console.error("[Onboarding] Failed to persist tip dismissal:", err),
    );
  },

  hydrateOnboarding: (state) => {
    if (!state) return;
    set((prev) => ({
      seenTours: union(prev.seenTours, state.toursSeen ?? []),
      dismissedTips: union(prev.dismissedTips, state.dismissedTips ?? []),
    }));
  },

  hasSeenTour: (tourId: string) => get().seenTours.includes(tourId),
  hasDismissedTip: (tipId: string) => get().dismissedTips.includes(tipId),
});
